import { createRoot } from 'react-dom/client';
import { useCallback, useEffect, useState } from 'react';
import './styles.css';

import { api, type Account, type Me } from './api';
import { link, navigate, usePath } from './router';
import { useTheme } from './theme';
import { Spinner } from './ui';
import {
  BookOpenIcon,
  FileSpreadsheetIcon,
  GlobeIcon,
  SparklesIcon,
  Icon,
  IconHover,
  LogOutIcon,
  MoonIcon,
  SettingsIcon,
  SunIcon,
  TriangleAlertIcon,
  Wordmark,
} from './icons';

import { LandingView } from './views/Landing';
import { AuthView } from './views/Auth';
import { EvidenceView } from './views/Evidence';
import { QuestionnairesView } from './views/Questionnaires';
import { WorkspaceView } from './views/Workspace';
import { GapsView } from './views/Gaps';
import { SettingsView } from './views/Settings';
import { TrustPageView } from './views/TrustPage';
import { PricingView } from './views/Pricing';
import { CheckoutCompleteView, CheckoutView } from './views/Checkout';
import { ForgotView, ResetView } from './views/Recover';
import { TrustView } from './views/Trust';
import { ContactView, PrivacyView, RefundsView, TermsView } from './views/Legal';

const NAV = [
  { to: '/evidence', label: 'Evidence', icon: BookOpenIcon },
  { to: '/questionnaires', label: 'Questionnaires', icon: FileSpreadsheetIcon },
  { to: '/gaps', label: 'Gaps', icon: TriangleAlertIcon },
  { to: '/trust-page', label: 'Trust page', icon: GlobeIcon },
  { to: '/settings', label: 'Settings', icon: SettingsIcon },
];

/**
 * The theme row.
 *
 * `.nav-item` rather than `.btn btn-ghost`, and not only for the alignment.
 * These sit at the bottom of the same column as the navigation, so they are
 * rows and should be built like rows — the button styling also gave them their
 * own padding and font size, half a step off the links directly above them.
 *
 * The alignment itself was not fixable with a utility. `.btn` is declared
 * unlayered in styles.css while Tailwind v4 puts its utilities in
 * `@layer utilities`, and unlayered rules beat layered ones whatever the
 * specificity — so `justify-start` sat in the class list doing nothing.
 */
function ThemeToggle() {
  const { isDark, setTheme } = useTheme();
  return (
    <IconHover
      as="button"
      className="nav-item w-full"
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
    >
      <Icon icon={isDark ? SunIcon : MoonIcon} size={16} />
      {isDark ? 'Light' : 'Dark'}
    </IconHover>
  );
}

function Sidebar({ account, onSignOut }: { account: Account; onSignOut: () => void }) {
  const path = usePath();

  return (
    <aside className="flex w-full shrink-0 flex-col border-b border-line bg-surface px-3 py-3 md:h-screen md:w-60 md:border-b-0 md:border-r md:px-3 md:py-5">
      <a
        {...link('/evidence')}
        className="mb-5 flex items-center gap-2 px-2 text-[15px] font-medium tracking-tight"
      >
        <span className="text-accent">
          <Wordmark size={20} />
        </span>
        Vouchkeep
      </a>

      <nav className="flex flex-row gap-1 overflow-x-auto md:flex-col">
        {NAV.map((item) => (
          <IconHover
            key={item.to}
            as="a"
            className="nav-item"
            data-active={path === item.to || path.startsWith(`${item.to}/`)}
            {...link(item.to)}
          >
            <Icon icon={item.icon} size={16} />
            {item.label}
          </IconHover>
        ))}
      </nav>

      <div className="mt-auto hidden flex-col gap-1 pt-6 md:flex">
        <IconHover as="a" className="nav-item" {...link('/pricing')}>
          <Icon icon={SparklesIcon} size={16} />
          Plans
        </IconHover>
        {account.trustPublished ? (
          <IconHover
            as="a"
            className="nav-item"
            href={`/trust/${account.trustSlug}`}
            target="_blank"
            rel="noreferrer"
          >
            <Icon icon={GlobeIcon} size={16} />
            Trust page
          </IconHover>
        ) : null}

        <ThemeToggle />

        <IconHover as="button" className="nav-item w-full" onClick={onSignOut}>
          <Icon icon={LogOutIcon} size={16} />
          Sign out
        </IconHover>

        <div className="mt-2 truncate border-t border-line px-2 pt-3 text-[12px] text-faint">
          {account.companyName}
        </div>
      </div>
    </aside>
  );
}

function App() {
  const path = usePath();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const account = me?.account ?? null;

  const refresh = useCallback(async () => {
    try {
      setMe(await api.me());
    } catch {
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Screens that change usage ask for a refresh rather than each keeping its own
  // copy of the counters, which would drift the moment two of them are open.
  const setAccount = useCallback((next: Account) => {
    setMe((current) => (current ? { ...current, account: next } : current));
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = async () => {
    await api.logout().catch(() => undefined);
    setMe(null);
    navigate('/');
  };

  // The trust page is public and deliberately outside the session: it has to
  // load for a stranger evaluating the company, which is the whole point of it.
  const trustMatch = path.match(/^\/trust\/([a-z0-9-]+)$/);
  if (trustMatch) return <TrustView slug={trustMatch[1]} />;

  if (path === '/pricing') {
    return <PricingView currentPlan={account?.plan} checkout={me?.checkout ?? 'off'} />;
  }

  // The legal pages are public and sit above the session check for the same
  // reason the trust page does: somebody deciding whether to buy has to be able
  // to read the terms and the refund policy without signing up first, and a
  // Polar reviewer reads them without an account at all.
  if (path === '/terms') return <TermsView signedIn={Boolean(account)} />;
  if (path === '/privacy') return <PrivacyView signedIn={Boolean(account)} />;
  if (path === '/refunds') return <RefundsView signedIn={Boolean(account)} />;
  if (path === '/contact') return <ContactView signedIn={Boolean(account)} />;

  // Getting back into an account has to work while signed out, and the reset
  // link arrives by mail rather than by navigation — so both live above the
  // session check, and the token comes off the query string.
  if (path === '/forgot') return <ForgotView />;
  if (path === '/reset') {
    return <ResetView token={new URLSearchParams(window.location.search).get('t') ?? ''} />;
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size={20} />
      </div>
    );
  }

  if (!account) {
    if (path === '/login' || path === '/signup') {
      return (
        <AuthView
          mode={path === '/login' ? 'login' : 'signup'}
          onAuthenticated={() => void refresh()}
        />
      );
    }
    return <LandingView />;
  }

  if (path === '/' || path === '/login' || path === '/signup') {
    navigate('/evidence', true);
  }

  // Checkout is its own full-width screen rather than a panel inside the app:
  // deciding to pay for something deserves a page with nothing else on it.
  if (path === '/checkout/complete') return <CheckoutCompleteView onActivated={refresh} />;

  const checkoutMatch = path.match(/^\/checkout\/(team|business)$/);
  if (checkoutMatch) {
    return (
      <CheckoutView
        plan={checkoutMatch[1] as 'team' | 'business'}
        account={account}
        mode={me?.checkout ?? 'off'}
        onUpdated={(next) => {
          setAccount(next);
          // The plan changed, so every limit the panel shows is stale.
          void refresh();
        }}
      />
    );
  }

  const workspaceMatch = path.match(/^\/questionnaires\/(qn_[a-z0-9]+)$/);

  return (
    <div className="flex min-h-screen flex-col md:flex-row">
      <Sidebar account={account} onSignOut={signOut} />
      <main className="min-w-0 flex-1 px-5 py-7 md:h-screen md:overflow-y-auto md:px-10 md:py-9">
        <div className="mx-auto max-w-5xl">
          {workspaceMatch ? (
            <WorkspaceView id={workspaceMatch[1]} />
          ) : path === '/questionnaires' ? (
            <QuestionnairesView />
          ) : path === '/gaps' ? (
            <GapsView />
          ) : path === '/trust-page' ? (
            <TrustPageView account={account} onUpdated={setAccount} />
          ) : path === '/settings' ? (
            <SettingsView
              account={account}
              plan={me!.plan}
              usage={me!.usage}
              onUpdated={setAccount}
              onPlanChanged={() => void refresh()}
            />
          ) : (
            <EvidenceView />
          )}
        </div>
      </main>
    </div>
  );
}

createRoot(document.getElementById('app')!).render(<App />);
